// Atoms for Flex order list (O1) and detail (O2). Ported from the design
// handoff (ui/flex-parcel-form-handoff-v2/.../flex_orders_screens.jsx).
//
// Status pill, RO section / row, and the universal OrderCard are kept here
// because they're only used by the orders flow. If they ever leak into a
// third screen, move them up into components/ui.

import type { ReactNode } from "react";
import { FLEX_STATUS_LABELS_RU } from "@mnp/shared";
import { Icon } from "../ui/Icon.js";

// ─── Flex status mapping ────────────────────────────────────────
// Лейблы берём из канонического @mnp/shared FLEX_STATUS_LABELS_RU, чтобы
// клиент не расходился с админкой/сервером. Раньше тут была локальная копия
// без `approved`/`pickup`/`customs`/`assigned`/`delivery`, и эти статусы
// падали в fallback «Отправлена» (баг 2026-05-30): клиент не видел «Принят в
// работу»/«Прибыло на склад». Тут оставляем ТОЛЬКО тона (цвета дизайн-хэндофф),
// лейбл идёт из канона. Псевдо-статусы ниже (pending_recipient_data/confirmed/
// cancelled) НЕ входят в канонический FlexStatus enum — их лейблы локальные.

export interface FlexStatusMeta {
  label: string;
  bg: string;
  fg: string;
  dot: string;
}

type FlexStatusTone = Omit<FlexStatusMeta, "label">;

const FLEX_STATUS_TONE: Record<string, FlexStatusTone> = {
  draft: { bg: "var(--mnp-gray-100)", fg: "var(--mnp-fg-2)", dot: "var(--mnp-fg-3)" },
  submitted: { bg: "#DCEBFE", fg: "#1F56B0", dot: "#1F56B0" },
  in_review: { bg: "#FFF6E6", fg: "#8A5A0B", dot: "#C68411" },
  approved: { bg: "#DEF2E7", fg: "#1E6E44", dot: "#1E6E44" },
  pickup: { bg: "#FEF3C7", fg: "#92400E", dot: "#D97706" },
  preparing: { bg: "#E6F4F1", fg: "#0E6E66", dot: "#0E9384" },
  customs: { bg: "#EFE5F7", fg: "#6C2EA3", dot: "#6C2EA3" },
  in_transit: { bg: "#EFE5F7", fg: "#6C2EA3", dot: "#6C2EA3" },
  assigned: { bg: "#E6F4F1", fg: "#0E6E66", dot: "#0E9384" },
  delivery: { bg: "#E6F4F1", fg: "#0E6E66", dot: "#0E9384" },
  delivered: { bg: "#1E6E44", fg: "#fff", dot: "#fff" },
  rejected: { bg: "#FDECED", fg: "#A40000", dot: "#C00000" },
  // Псевдо-статусы (не из канонического enum) — лейбл из FLEX_PSEUDO_LABELS.
  pending_recipient_data: { bg: "#FFF6E6", fg: "#8A5A0B", dot: "#C68411" },
  confirmed: { bg: "#DEF2E7", fg: "#1E6E44", dot: "#1E6E44" },
  cancelled: { bg: "var(--mnp-gray-100)", fg: "var(--mnp-fg-2)", dot: "var(--mnp-fg-3)" },
};

const FLEX_PSEUDO_LABELS: Record<string, string> = {
  pending_recipient_data: "Ждёт данных",
  confirmed: "Подтверждена",
  cancelled: "Отменена",
};

const SUBMITTED_TONE: FlexStatusTone = FLEX_STATUS_TONE.submitted!;

export function flexStatusMeta(status: string): FlexStatusMeta {
  const tone = FLEX_STATUS_TONE[status] ?? SUBMITTED_TONE;
  const label =
    (FLEX_STATUS_LABELS_RU as Record<string, string>)[status] ??
    FLEX_PSEUDO_LABELS[status] ??
    FLEX_STATUS_LABELS_RU.submitted;
  return { ...tone, label };
}

export function FlexStatusPill({ status }: { status: string }) {
  const meta = flexStatusMeta(status);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: meta.bg,
        color: meta.fg,
        fontSize: 11,
        fontWeight: 700,
        padding: "3px 9px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: meta.dot }} />
      {meta.label}
    </span>
  );
}

// ─── Packaging mini-badge ──────────────────────────────────────
export function PackagingBadge() {
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: ".06em",
        textTransform: "uppercase",
        background: "var(--mnp-teal-100)",
        color: "var(--mnp-teal-800)",
        padding: "1px 6px",
        borderRadius: 4,
        flexShrink: 0,
      }}
    >
      + упаковка
    </span>
  );
}

// ─── Read-only Section / Row (O2 detail) ───────────────────────
export function ORoSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      style={{
        marginBottom: 12,
        padding: "12px 14px",
        background: "var(--mnp-bg-alt)",
        border: "1px solid var(--mnp-border-soft)",
        borderRadius: 12,
      }}
    >
      <p
        className="t-eyebrow"
        style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mnp-fg-3)" }}
      >
        {title}
      </p>
      {children}
    </div>
  );
}

export function ORoRow({
  k,
  v,
  mono,
  muted,
  link,
}: {
  k: string;
  v: ReactNode;
  mono?: boolean;
  muted?: boolean;
  link?: string;
}) {
  const valueNode = link ? (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "var(--mnp-teal-700)", textDecoration: "none", fontWeight: 500 }}
    >
      {v}
    </a>
  ) : (
    v
  );
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        padding: "4px 0",
        fontSize: 13,
        gap: 12,
      }}
    >
      <span style={{ color: "var(--mnp-fg-3)", flexShrink: 0 }}>{k}</span>
      <span
        style={{
          color: muted ? "var(--mnp-fg-3)" : "var(--mnp-fg-1)",
          fontWeight: muted ? 400 : 500,
          textAlign: "right",
          fontFamily: mono ? "var(--mnp-font-mono)" : undefined,
          fontStyle: muted ? "italic" : undefined,
          wordBreak: "break-word",
        }}
      >
        {valueNode}
      </span>
    </div>
  );
}

// ─── Pre-order inline warn block (O1 card) ─────────────────────
export function PreorderWarnBlock({
  text,
  onClick,
}: {
  text: string;
  onClick: () => void;
}) {
  return (
    <div
      style={{
        marginTop: 6,
        padding: "8px 10px",
        background: "#FFF6E6",
        border: "1px solid #F2D88C",
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <Icon name="alertCircle" size={14} color="#B6480C" stroke={2} />
      <span
        style={{
          flex: 1,
          fontSize: 11.5,
          color: "#6B4709",
          fontWeight: 500,
          lineHeight: 1.3,
        }}
      >
        {text}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        style={{
          background: "#E37511",
          color: "#fff",
          border: 0,
          padding: "6px 12px",
          borderRadius: 8,
          fontFamily: "inherit",
          fontSize: 11.5,
          fontWeight: 700,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          whiteSpace: "nowrap",
        }}
      >
        Заполнить →
      </button>
    </div>
  );
}

// ─── Loading skeleton card ─────────────────────────────────────
export function OrderCardSkeleton() {
  return (
    <div
      className="card card-tight"
      style={{
        marginBottom: 10,
        minHeight: 90,
        background: "var(--mnp-bg-alt)",
        borderColor: "var(--mnp-border-soft)",
      }}
    >
      <div
        style={{
          height: 14,
          width: "55%",
          background: "var(--mnp-gray-200)",
          borderRadius: 6,
          marginBottom: 10,
        }}
      />
      <div
        style={{
          height: 12,
          width: "80%",
          background: "var(--mnp-gray-200)",
          borderRadius: 6,
          marginBottom: 8,
        }}
      />
      <div
        style={{
          height: 12,
          width: "35%",
          background: "var(--mnp-gray-200)",
          borderRadius: 6,
        }}
      />
    </div>
  );
}

// ─── Pre-order missing fields → human label ────────────────────
export function preorderMissingLabel({
  missingPhone,
  missingAddress,
}: {
  missingPhone?: boolean | null;
  missingAddress?: boolean | null;
}): string {
  if (missingPhone && missingAddress) return "Дозаполните телефон и адрес получателя";
  if (missingPhone) return "Дозаполните телефон получателя";
  if (missingAddress) return "Дозаполните адрес получателя";
  return "Дозаполните данные получателя";
}
